import React, { useState, useEffect } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { apiService } from '../api/apiclient';
import AccountCard from './AccountCard';
import PlatformConnectModal from '../components/PlatformConnectModal';
import { globalStore } from '../configs/constant';

const AccountPage = () => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showTokenSubmitModal, setShowTokenSubmitModal] = useState(false);
    const [currentAccount, setCurrentAccount] = useState(null);

    const fetchAccounts = async () => {
        try {
            if (globalStore.accounts.length > 0 && !globalStore.isDirty) {
                setAccounts(globalStore.accounts);
            } else {
                setLoading(true);
                const response = await apiService.listAccounts();
                const data = response.accounts || [];
                setAccounts(data);
                globalStore.accounts = data;
                globalStore.isDirty = false;
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    const handleRefresh = async (account) => {
        try {
            setLoading(true);
            setCurrentAccount(account);
            const response = await apiService.getAuthUrl(account.platform);
            if (response.url) {
                window.location.href = response.url;
            } else {
                alert(response.message);
                if (onClose) onClose();
            }
        } catch (err) {
            alert('Authorization failed to start');
        } finally {
            setLoadingId(false);
            setCurrentAccount(null);
        }
    };

    const handleSubmitToken = async (platform, data) => {
        try {
            const response = await apiService.submitToken(platform, data);
            if (response.success) {
                window.location.href = '/accounts';
            }
        } catch (err) {
            alert('Authorization failed to start');
            setLoadingId(null);
        }
    };

    const handleDisconnect = async (id) => {
        if (!confirm("Are you sure you want to disconnect this account?")) return;

        try {
            await apiService.deleteAccount(id);
            setAccounts(prev => prev.map(account => {
                if (account.id === id) {
                    return {
                        ...account,
                        status: 'inactive',
                    };
                }
                return account;
            }));
        } catch (err) {
            console.error(err);
            alert("Failed to deactivate account. Please try again.");
        }
    };

    const handleReconnect = async (id) => {
        if (!confirm("Are you sure you want to disconnect this account?")) return;

        try {
            const response = await apiService.reconnectAccount(id);
            if (response.success) {
                setAccounts(prev => prev.map(account => {
                    if (account.id === id) {
                        return {
                            ...account,
                            status: 'active',
                        };
                    }
                    return account;
                }));
            } else {
                alert(response.message || "Reconnect failed");
            }
        } catch (err) {
            console.error(err);
            alert("Failed to reconnect account. Please try again.");
        }
    };

    return (
        <div className="p-8 max-w-7xl mx-auto">
            <header className="flex justify-between items-center mb-10">
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Accounts</h1>
                    <p className="text-gray-500 mt-1">
                        {loading ? "Loading..." : `Active Channels: ${accounts.length}`}
                    </p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-red-600 text-white px-6 py-3 rounded-2xl font-bold hover:bg-red-700 transition shadow-xl shadow-red-100 flex items-center gap-2"
                >
                    <Plus size={20} /> Add New Account
                </button>
            </header>

            {loading && accounts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64">
                    <Loader2 className="animate-spin text-red-500 mb-4" size={40} />
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {accounts.map(acc => (
                        <AccountCard
                            key={acc.id}
                            account={acc}
                            onRefresh={() => handleRefresh(acc)}
                            onDisconnect={() => handleDisconnect(acc.id)}
                            onReconnect={() => handleReconnect(acc.id)}
                        />
                    ))}

                    {!loading && (
                        <div
                            onClick={() => setIsModalOpen(true)}
                            className="border-2 border-dashed border-gray-200 rounded-3xl p-8 flex flex-col items-center justify-center text-gray-400 hover:border-red-300 hover:text-red-400 cursor-pointer transition-all"
                        >
                            <Plus size={40} className="mb-2" />
                            <p className="font-medium">Add Account</p>
                        </div>
                    )}
                </div>
            )}
            {showTokenSubmitModal && currentAccount && <TokenSubmitModal
                platform={currentAccount.platform}
                isOpen={showTokenSubmitModal}
                onClose={() => setShowTokenSubmitModal(false)}
                onSubmit={handleSubmitToken}
            />}
            <PlatformConnectModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={fetchAccounts}
            />
        </div>
    );
};

export default AccountPage;